// src/utils/inlineMarkdown.js
//
// Tokenizer for a minimal inline-markdown subset used in project
// descriptions: `code`, **bold**, *italic*, [text](url). Pure function —
// rendering the tokens to React elements happens in ProjectArticle.
//
// Deliberately flat: no nesting (bold inside links etc.). Anything that
// doesn't match falls through as plain text, so malformed input can never
// break rendering.

const TOKEN_RE =
  /(`[^`\n]+`)|(\*\*[^*\n]+\*\*)|(\*[^*\n]+\*)|(\[[^\]\n]+\]\([^)\s]+\))/g;

const LINK_RE = /^\[([^\]]+)\]\(([^)\s]+)\)$/;

/**
 * @param {string} text
 * @returns {Array<{type: 'text'|'code'|'bold'|'italic'|'link', text: string, href?: string}>}
 */
export function parseInline(text) {
  if (!text) return [];

  const tokens = [];
  let lastIndex = 0;

  TOKEN_RE.lastIndex = 0;
  let match;
  while ((match = TOKEN_RE.exec(text)) !== null) {
    if (match.index > lastIndex) {
      tokens.push({ type: 'text', text: text.slice(lastIndex, match.index) });
    }

    const [raw, code, bold, italic, link] = match;
    if (code) {
      tokens.push({ type: 'code', text: raw.slice(1, -1) });
    } else if (bold) {
      tokens.push({ type: 'bold', text: raw.slice(2, -2) });
    } else if (italic) {
      tokens.push({ type: 'italic', text: raw.slice(1, -1) });
    } else if (link) {
      const parts = raw.match(LINK_RE);
      // Only linkify http(s) targets; anything else stays visible as text.
      if (parts && /^https?:\/\//i.test(parts[2])) {
        tokens.push({ type: 'link', text: parts[1], href: parts[2] });
      } else {
        tokens.push({ type: 'text', text: raw });
      }
    }

    lastIndex = match.index + raw.length;
  }

  if (lastIndex < text.length) {
    tokens.push({ type: 'text', text: text.slice(lastIndex) });
  }

  return tokens;
}
